import { useState } from "react";
import StudentFilter from "types/StudentFilter";
import { Avatar } from "./ui/Avatar";
import Student, { GradeLevelGroup } from "types/Student";
import { Modal } from "./ui/Modal";
import { AddStudentForm } from "./AddStudentForm";
import { useAuth } from "context/AuthContext";
import { PlanBadge } from "./PlanBadge";
import { useNavigate } from "react-router-dom";
import { StudentMenu } from "./StudentMenu";
import { useStudents } from "context/StudentsContext";

interface StudentListProps {
  filter: StudentFilter;
  students: Student[];
  selectedStudentId: number | null;
  onSelectStudent: (studentId: number) => void;
  onStudentAdded: () => void;
  gradeLevelGroups: GradeLevelGroup[];
}

export function StudentList({
  filter,
  students,
  selectedStudentId,
  onSelectStudent,
  onStudentAdded,
  gradeLevelGroups,
}: StudentListProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { planLimit } = useAuth();
  const navigate = useNavigate();
  const { activeStudentsCount } = useStudents();

  const handleAddClick = () => {
    if (activeStudentsCount >= planLimit) {
      navigate("/settings");
    } else {
      setIsAddModalOpen(true);
    }
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-xs lg:text-sm font-bold uppercase text-gray-500">
          Students
        </h2>
        <button
          onClick={handleAddClick}
          className="inline-flex items-center justify-center w-6 h-6 lg:w-8 lg:h-8 rounded-full text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative"
          aria-label="Add Student"
        >
          <svg
            className="w-4 h-4 lg:w-5 lg:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          <div className="absolute -top-1 -right-1 lg:-top-1 lg:-right-1">
            <PlanBadge
              studentCount={activeStudentsCount}
              planLimit={planLimit}
            />
          </div>
        </button>
      </div>
      <ul className="flex flex-col space-y-1 text-xs lg:text-sm list-none p-0">
        {students
          .filter((student) => student.teacher_student_status !== "pending")
          .map((student) => (
            <li
              key={student.id}
              className={`student-list-item flex items-center justify-between px-2 py-1 rounded cursor-pointer lg:hover:bg-gray-100 group ${
                selectedStudentId === student.id
                  ? "bg-blue-100 text-blue-700 font-semibold"
                  : ""
              }`}
            >
              <div
                className={`flex items-center space-x-2 flex-1 ${
                  student.status === "inactive" ? "opacity-50" : ""
                }`}
                onClick={() => onSelectStudent(student.id)}
                onTouchEnd={(e) => {
                  // Prevent default to avoid double-tap zoom and ensure click fires
                  e.preventDefault();
                  onSelectStudent(student.id);
                }}
              >
                <Avatar />
                <span className="truncate">{student.name}</span>
              </div>
              <div className="flex items-center space-x-1 lg:space-x-2">
                <span
                  className={`text-xs text-gray-400 ${
                    student.status === "inactive" ? "opacity-50" : ""
                  }`}
                >
                  {student.lastSeen}
                </span>
                <StudentMenu
                  studentId={student.id}
                  status={student.status}
                  onStatusChange={onStudentAdded}
                />
              </div>
            </li>
          ))}
      </ul>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Student"
      >
        <AddStudentForm
          preSelectedSubject={filter.subject}
          preSelectedGradeLevel={filter.gradeLevel}
          gradeLevelGroups={gradeLevelGroups}
          onSuccess={onStudentAdded}
          onClose={() => setIsAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
